"use client";

import { useState } from "react";
import { Check, Copy, Loader2, TriangleAlert } from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  DEFAULT_VA_LABELS,
  type QrisLabels,
} from "./qris-payment-step.helpers";
import {
  VA_BANK_OPTIONS,
  type VaBank,
  type VirtualAccount,
} from "./use-virtual-account";


interface VirtualAccountPanelProps {
  labels: QrisLabels;
  selectedBank: VaBank;
  account: VirtualAccount | null;
  pendingBank: VaBank | null;
  error: string | null;
  onSelectBank: (bank: VaBank) => void;
}

/**
 * Renders the virtual account inline. The number never lives behind a redirect:
 * sending a mobile buyer to a hosted page is the app-switch that the VA rail
 * exists to avoid.
 */
export function VirtualAccountPanel({
  labels,
  selectedBank,
  account,
  pendingBank,
  error,
  onSelectBank,
}: VirtualAccountPanelProps) {
  const va = labels.va ?? DEFAULT_VA_LABELS;
  const [copied, setCopied] = useState(false);
  const showsAccount = account !== null && account.bank === selectedBank;

  /**
   * Grouped for reading only. A virtual account number is typed into a banking
   * app by hand, and an unbroken run of digits is where the buyer slips. The
   * clipboard still gets the raw value, which is what the app expects.
   */
  function groupDigits(value: string) {
    const groups = value.match(/.{1,4}/g) ?? [value];

    // Indonesian VA numbers run 10 to 16 digits, so a plain group-of-four can
    // strand a single digit at the end. Fold that one back into its neighbour.
    if (groups.length > 1 && groups[groups.length - 1].length === 1) {
      const stray = groups.pop() ?? "";
      groups[groups.length - 1] += stray;
    }

    return groups.join(" ");
  }

  async function copyAccountNumber() {
    if (!account) {
      return;
    }

    try {
      await navigator.clipboard.writeText(account.accountNumber);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch (cause) {
      console.error("Could not copy virtual account number", cause);
    }
  }

  return (
    <div className="flex w-full min-w-0 flex-col gap-4">
      {/*
        No heading here. The selected tab already names the method, so an
        h3 repeating it was the same word twice in a row.
      */}
      <p className="min-w-0 break-words text-sm text-text-muted">
        {va.instruction}
      </p>

      <div className="min-w-0 space-y-2">
        <p className="text-xs uppercase tracking-wide text-text-muted">
          {va.bankLabel}
        </p>
        {/*
          A grid, not flex-wrap: five chips of uneven width wrapped 4 + 1 and
          left Permata stranded on its own row.
        */}
        <div
          className="grid grid-cols-2 gap-2 @sm:grid-cols-3"
          role="group"
          aria-label={va.bankLabel}
        >
          {VA_BANK_OPTIONS.map((bank) => {
            const isSelected = bank.code === selectedBank;

            return (
              <Button
                key={bank.code}
                type="button"
                variant={isSelected ? "default" : "outline"}
                aria-pressed={isSelected}
                aria-busy={pendingBank === bank.code}
                onClick={() => onSelectBank(bank.code)}
                className={cn(
                  "min-h-11 w-full min-w-0 justify-center px-3",
                  isSelected &&
                    "bg-brand-primary text-white hover:bg-brand-primary-hover",
                )}
              >
                {pendingBank === bank.code ? (
                  <Loader2
                    className="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none"
                    aria-hidden="true"
                  />
                ) : null}
                {bank.label}
              </Button>
            );
          })}
        </div>
      </div>

      <div className="min-w-0 rounded-2xl border border-border-primary bg-surface-secondary p-4">
        <p className="text-xs uppercase tracking-wide text-text-muted">
          {va.accountLabel}
        </p>

        {showsAccount ? (
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <p
              className="min-w-0 break-all font-mono text-2xl font-bold tracking-wide text-text-heading @sm:text-3xl"
              aria-label={account.accountNumber}
            >
              {groupDigits(account.accountNumber)}
            </p>
            <Button
              type="button"
              variant="outline"
              onClick={copyAccountNumber}
              className="min-h-11 gap-2"
            >
              {copied ? (
                <Check className="h-4 w-4" aria-hidden="true" />
              ) : (
                <Copy className="h-4 w-4" aria-hidden="true" />
              )}
              {copied ? va.copied : va.copy}
            </Button>
          </div>
        ) : (
          <div className="mt-2 flex h-9 items-center" aria-live="polite">
            <Loader2
              className="h-5 w-5 animate-spin text-brand-primary motion-reduce:animate-none"
              aria-hidden="true"
            />
          </div>
        )}

        <p className="mt-3 text-xs text-text-muted">
          {va.expiryHint}
        </p>
      </div>

      {error ? (
        <div
          className="flex min-w-0 items-center gap-2 text-sm text-status-error-text"
          role="alert"
        >
          <TriangleAlert className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span className="min-w-0 break-words">{error}</span>
        </div>
      ) : null}
    </div>
  );
}
